import { isCardPermanentlyBlocked } from "./cardStatus"
import { getFriendlyErrorMessage } from "./errors"

const POLL_INTERVAL_MS = 10000
const SLOW_POLL_INTERVAL_MS = 30000
const SLOW_AFTER_MS = 120000
const STATUS_ERROR_BACKOFF_COUNT = 3

export interface CardBlockNotice {
	phase: "pending" | "success" | "warning" | "error"
	message: string
}

const STAGES: Record<NonNullable<ICardBlockStatus["stage"]>, string> = {
	unfreeze: "Preparing the card for balance withdrawal...",
	withdraw: "Returning the remaining card balance...",
	block: "Closing the card at the provider..."
}

interface MonitorOptions {
	card: ICardSaved
	operationUid: string
	readStatus: () => Promise<IAPIResponse<ICardBlockStatus>>
	readCard: () => Promise<IAPIResponse<ICardSaved>>
	onCard: (card: ICardSaved) => void
	onNotice: (notice: CardBlockNotice, settled: boolean) => void
	onSettled: () => void
}

// One serial read-only monitor. A timeout means uncertainty, not failure.
// Manual refresh joins an in-flight check; it never starts another block.
export const createCardBlockMonitor = (options: MonitorOptions) => {
	const startedAt = Date.now()
	let stopped = false
	let settled = false
	let completed = false
	let timer: ReturnType<typeof setTimeout> | undefined
	let inFlight: Promise<void> | undefined
	let lastCard = options.card
	let statusErrors = 0

	const run = async () => {
		let cardResponse: IAPIResponse<ICardSaved> | undefined
		const cardRead = Promise.resolve().then(options.readCard).then(
			response => { cardResponse = response },
			() => undefined
		)
		const statusResponse = await Promise.resolve().then(options.readStatus).catch(() => undefined)
		if (stopped) return
		const operation = !statusResponse?.error && statusResponse?.data?.operation_uid === options.operationUid &&
			statusResponse.data.card_uid === options.card.uid ? statusResponse.data : undefined
		// A completed block is authoritative even if cards/get never responds.
		// Pending/failed operations still use the fresh card to explain the
		// difference between closure and completion of the balance return.
		if (operation?.status !== "completed") await cardRead
		if (stopped) return
		const freshCard = !cardResponse?.error && cardResponse?.data?.uid === options.card.uid
			? cardResponse.data : undefined
		statusErrors = operation ? 0 : statusErrors + 1
		if (freshCard) {
			lastCard = isCardPermanentlyBlocked(lastCard.status) && !isCardPermanentlyBlocked(freshCard.status)
				? { ...freshCard, status: lastCard.status } : freshCard
		}

		let notice: CardBlockNotice
		if (operation?.status === "completed") {
			// The completed operation is authoritative backend confirmation even
			// when cards/get still returns its previous snapshot.
			lastCard = { ...lastCard, status: isCardPermanentlyBlocked(lastCard.status) ? lastCard.status : "blocked", blocking: false, block_operation_uid: null }
			settled = true
			completed = true
			notice = freshCard
				? { phase: "success", message: "Card blocked successfully." }
				: { phase: "warning", message: "Card blocked, but the updated status could not be loaded. Refresh the card manually." }
		} else if (operation?.status === "failed") {
			settled = true
			const reason = getFriendlyErrorMessage(operation.error_message, "Card block failed.")
			notice = {
				phase: "error",
				message: isCardPermanentlyBlocked(lastCard.status)
					? `Card is blocked, but the block operation reported an error: ${reason} Contact support to verify the balance return.`
					: reason
			}
		} else if (isCardPermanentlyBlocked(lastCard.status)) {
			notice = { phase: "pending", message: "Card is blocked. Checking completion of the balance return..." }
		} else if (statusErrors > 0) {
			notice = { phase: "warning", message: "Block status is temporarily unavailable. This does not mean blocking failed. Checking again automatically; you can also use Refresh status." }
		} else if (Date.now() - startedAt >= SLOW_AFTER_MS) {
			notice = { phase: "warning", message: "Blocking is taking longer than expected. Completion is not confirmed yet. Checking continues automatically; you can also use Refresh status." }
		} else {
			notice = { phase: "pending", message: (operation?.stage && STAGES[operation.stage]) || "Card block is being processed..." }
		}
		if (freshCard || operation?.status === "completed") options.onCard(lastCard)
		options.onNotice(notice, settled)
		if (settled) options.onSettled()
	}

	const check = (): Promise<void> => {
		if (stopped || settled) return Promise.resolve()
		if (inFlight) return inFlight
		clearTimeout(timer)
		inFlight = run().finally(() => {
			inFlight = undefined
			if (!stopped && !settled) {
				const delay = statusErrors >= STATUS_ERROR_BACKOFF_COUNT || Date.now() - startedAt >= SLOW_AFTER_MS
					? SLOW_POLL_INTERVAL_MS : POLL_INTERVAL_MS
				timer = setTimeout(() => void check(), delay)
			}
		})
		return inFlight
	}

	return {
		check,
		isSettled: () => settled,
		isCompleted: () => completed,
		stop: () => { stopped = true; clearTimeout(timer) }
	}
}
